import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import TemplatePhonePreview from '../components/TemplatePhonePreview';

const fmtMoney = v => `$${v >= 0.01 || v === 0 ? v.toFixed(2) : v.toFixed(4)}`;

// Upload a template header file to Meta's media servers (WhatsApp headers)
function WAHeaderUpload({ format, mediaId, previewUrl, onChange }) {
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);
  const accept = format === 'IMAGE' ? 'image/jpeg,image/png'
    : format === 'VIDEO' ? 'video/mp4'
    : 'application/pdf';

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const r = await api.post('/uploads/wa-media', fd);
      onChange(r.data);
      toast.success('Uploaded to WhatsApp — ready to send');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed', { duration: 8000 });
    }
    setBusy(false);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }} onChange={handleFile} />
      {mediaId ? (
        <>
          {format === 'IMAGE' && previewUrl
            ? <img src={previewUrl} alt="header" style={{ height: 44, width: 66, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
            : <span style={{ fontSize: 22 }}>{format === 'VIDEO' ? '🎬' : '📄'}</span>}
          <span style={{ fontSize: 12, color: 'var(--success)' }}>✓ Uploaded to WhatsApp</span>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => onChange(null)} title="Remove">✕</button>
        </>
      ) : (
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? 'Uploading…' : `⬆ Upload header ${format.toLowerCase()}`}
        </button>
      )}
    </div>
  );
}

// Upload button + preview chip for one media slot (email header image/video)
function MediaUpload({ kind, value, onChange }) {
  const [busy, setBusy] = React.useState(false);
  const inputRef = React.useRef(null);
  const accept = kind === 'image' ? 'image/jpeg,image/png,image/gif,image/webp' : 'video/mp4,video/webm,video/quicktime';

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const r = await api.post('/uploads', fd);
      onChange(r.data.url);
      toast.success(`${kind === 'image' ? 'Image' : 'Video'} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    }
    setBusy(false);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }} onChange={handleFile} />
      {value ? (
        <>
          {kind === 'image'
            ? <img src={value} alt="header" style={{ height: 44, width: 66, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
            : <span style={{ fontSize: 22 }}>🎬</span>}
          <a href={value} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, color: 'var(--primary-light)', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value.split('/').pop()}
          </a>
          <button type="button" className="btn btn-danger btn-sm" onClick={() => onChange('')} title="Remove">✕</button>
        </>
      ) : (
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? 'Uploading…' : kind === 'image' ? '🖼️ Upload image' : '🎬 Upload video'}
        </button>
      )}
    </div>
  );
}

// Highest {{n}} in a template body → number of params the template needs
function detectParamCount(text) {
  const nums = [...String(text || '').matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map(m => parseInt(m[1], 10));
  return nums.length ? Math.max(...nums) : 0;
}

// Sample contact used to render {{name}}/{{phone}}/{{email}} in the preview
const SAMPLE_CONTACT = { name: 'Tendai', phone: '+263 77 123 4567', email: 'tendai@example.com' };
const fillSample = v => String(v || '')
  .replace(/\{\{\s*name\s*\}\}/gi, SAMPLE_CONTACT.name)
  .replace(/\{\{\s*phone\s*\}\}/gi, SAMPLE_CONTACT.phone)
  .replace(/\{\{\s*email\s*\}\}/gi, SAMPLE_CONTACT.email);

export default function NewBroadcast() {
  const nav = useNavigate();
  const { user } = useAuth();
  const sharedWaba = user?.workspaces?.some(workspace => workspace.linked && workspace.id === user.active_workspace);
  const businessName = user?.wa_config?.business_name || user?.name || 'Your Business';
  const [tags, setTags] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateError, setTemplateError] = useState(null);
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState({
    name: '',
    channel: 'whatsapp',
    template_name: '',
    template_language: 'en_US',
    template_category: 'MARKETING',
    template_params: [''],
    template_param_fallbacks: [''],
    wa_header_type: '',
    wa_header_media_id: '',
    wa_header_text: '',
    wa_header_preview: '',
    subject: '',
    html_body: '',
    text_body: '',
    email_header_image: '',
    email_header_video: '',
    tags: [],
    all_contacts: false,
  });
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [shorteningLink, setShorteningLink] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState(null);
  // While true, the email content mirrors the WhatsApp template (channel "both").
  // Editing any email field manually turns mirroring off.
  const [emailAuto, setEmailAuto] = useState(true);
  // Test-send modal
  const [testOpen, setTestOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testEmail, setTestEmail] = useState(user?.email || '');
  const [testSending, setTestSending] = useState(false);

  // Live cost estimate — recomputed whenever the audience or channel changes
  useEffect(() => {
    let alive = true;
    setEstimate(null);
    api.post('/broadcasts/estimate', {
      channel: form.channel, all_contacts: form.all_contacts, tags: form.tags,
      template_category: form.template_category,
    })
      .then(r => { if (alive) setEstimate(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [form.channel, form.all_contacts, form.tags, form.template_category]);

  useEffect(() => {
    api.get('/tags').then(r => setTags(r.data));
    api.get('/templates')
      .then(r => { setTemplates(r.data); setTemplateError(null); })
      .catch(err => {
        const msg = err.response?.data?.message || 'Failed to load templates';
        setTemplateError(msg);
      });
    api.get('/links').then(r => setLinks(r.data)).catch(() => {});
  }, []);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function setParam(i, v) {
    const p = [...form.template_params];
    p[i] = v;
    set('template_params', p);
  }

  function setFallback(i, v) {
    const p = [...(form.template_param_fallbacks || [])];
    p[i] = v;
    set('template_param_fallbacks', p);
  }

  // Selecting an approved template auto-sizes the param inputs to what the
  // body actually uses and adopts the template's language code.
  useEffect(() => {
    const t = templates.find(x => x.name === form.template_name);
    if (!t) return;
    const n = detectParamCount(t.components?.find(c => c.type === 'BODY')?.text);
    setForm(f => ({
      ...f,
      template_language: t.language || f.template_language,
      template_category: t.category || 'MARKETING',
      template_params: Array.from({ length: n }, (_, i) => f.template_params[i] || ''),
      template_param_fallbacks: Array.from({ length: n }, (_, i) => (f.template_param_fallbacks || [])[i] || ''),
      // header media belongs to the previous template — start fresh
      wa_header_type: '', wa_header_media_id: '', wa_header_text: '', wa_header_preview: '',
    }));
  }, [form.template_name, templates]);

  // Channel "both": keep the email content in sync with the WhatsApp template.
  // {{n}} params resolve to their value → fallback; contact placeholders like
  // {{name}} are kept and filled per recipient at send time.
  useEffect(() => {
    if (!emailAuto || form.channel !== 'both') return;
    const t = templates.find(x => x.name === form.template_name);
    if (!t) return;
    const comps = t.components || [];
    const resolve = txt => String(txt || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (m, d) => {
      const i = parseInt(d, 10) - 1;
      return (form.template_params[i] || '').trim() || ((form.template_param_fallbacks || [])[i] || '').trim() || m;
    });
    const header = comps.find(c => c.type === 'HEADER');
    const headerText = header?.format === 'TEXT' ? resolve(header.text) : '';
    const body = resolve(comps.find(c => c.type === 'BODY')?.text || '');
    const footer = comps.find(c => c.type === 'FOOTER')?.text || '';
    const urlButtons = (comps.find(c => c.type === 'BUTTONS')?.buttons || []).filter(b => b.type === 'URL');

    const esc = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html =
      '<div style="font-family:Arial,sans-serif;max-width:560px;margin:0 auto;line-height:1.6">' +
      (headerText ? `<h2>${esc(headerText)}</h2>` : '') +
      `<p>${esc(body).replace(/\n/g, '<br>')}</p>` +
      urlButtons.map(b => `<p><a href="${resolve(b.url)}" style="background:#25d366;color:#fff;padding:10px 20px;border-radius:8px;text-decoration:none;font-weight:600">${esc(b.text)}</a></p>`).join('') +
      (footer ? `<p style="color:#64748b;font-size:12px">${esc(footer)}</p>` : '') +
      '</div>';
    const text = [headerText, body, ...urlButtons.map(b => `${b.text}: ${resolve(b.url)}`), footer]
      .filter(Boolean).join('\n\n');
    const subject = headerText || t.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    setForm(f => ({ ...f, subject, text_body: text, html_body: html }));
  }, [emailAuto, form.channel, form.template_name, form.template_params, form.template_param_fallbacks, templates]);

  async function createShortLink() {
    const originalUrl = newLinkUrl.trim();
    if (!originalUrl) return toast.error('Enter a destination URL');
    try {
      const parsed = new URL(originalUrl);
      if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('invalid protocol');
    } catch {
      return toast.error('Enter a full URL beginning with http:// or https://');
    }

    setShorteningLink(true);
    try {
      const r = await api.post('/links', { original_url: originalUrl });
      if (r.data?.queued) {
        toast.error('You are offline. Reconnect before creating a short link.');
        return;
      }
      setLinks(ls => [r.data, ...ls]);
      setNewLinkUrl('');
      toast.success('Short link created');
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Could not create the short link');
    } finally {
      setShorteningLink(false);
    }
  }

  async function copyShortLink(shortUrl) {
    try {
      await navigator.clipboard.writeText(shortUrl);
      toast.success('Short link copied');
    } catch {
      toast.error('Could not copy the link. Select it and copy manually.');
    }
  }

  async function handleSave(action = 'draft') {
    if (!form.name) return toast.error('Broadcast name required');
    const needsWA    = form.channel === 'whatsapp' || form.channel === 'both';
    const needsEmail = form.channel === 'email'    || form.channel === 'both';
    if (action !== 'draft') {
      if (needsWA && !form.template_name) return toast.error('Select or enter a WhatsApp template');
      if (needsEmail && !form.subject) return toast.error('Email subject required');
    }
    if (action === 'schedule' && !scheduledAt) return toast.error('Pick a date and time to schedule');
    if (action === 'schedule' && new Date(scheduledAt) <= new Date())
      return toast.error('Scheduled time must be in the future');
    // Every auto-detected parameter needs a value or a fallback before sending
    if (action !== 'draft' && needsWA && selectedTemplate && paramCount > 0) {
      for (let i = 0; i < paramCount; i++) {
        if (!(form.template_params[i] || '').trim() && !((form.template_param_fallbacks || [])[i] || '').trim())
          return toast.error(`Parameter {{${i + 1}}} needs a value or a fallback`);
      }
    }
    if (action !== 'draft' && needsWA && headerNeedsMedia && !form.wa_header_media_id)
      return toast.error(`This template needs a header ${tHeader.format.toLowerCase()} — upload it in the WhatsApp Template card`);
    if (action !== 'draft' && needsWA && headerNeedsText && !form.wa_header_text.trim())
      return toast.error('This template needs a header text value');
    setSaving(true);
    try {
      // Keep positions aligned with {{n}} — only strip fully-empty trailing rows
      const params = form.template_params.map(s => (s || '').trim());
      const fallbacks = (form.template_param_fallbacks || []).map(s => (s || '').trim());
      while (params.length && !params[params.length - 1] && !fallbacks[params.length - 1]) {
        params.pop(); fallbacks.pop();
      }
      const r = await api.post('/broadcasts', { ...form, template_params: params, template_param_fallbacks: fallbacks });
      // channel "both" returns a WhatsApp + email pair; send each one
      const pairs = r.data.both
        ? r.data.broadcasts.map(b => ({ id: b._id, channel: b.channel }))
        : [{ id: r.data._id, channel: form.channel }];
      if (action === 'send' || action === 'schedule') {
        const body = action === 'schedule' ? { scheduled_at: new Date(scheduledAt).toISOString() } : {};
        let total = 0;
        let cost = 0;
        const failures = [];
        for (const { id, channel } of pairs) {
          try {
            const s = await api.post(`/broadcasts/${id}/send`, body);
            total += s.data.total;
            cost += s.data.estimated_cost || 0;
          } catch (e) {
            failures.push(`${channel}: ${e.response?.data?.error || 'failed'}`);
          }
        }
        if (total > 0) {
          const costNote = cost > 0 ? ` · est. cost ${fmtMoney(cost)}` : '';
          toast.success(action === 'schedule'
            ? `Scheduled for ${new Date(scheduledAt).toLocaleString()} (${total} recipient${total === 1 ? '' : 's'})${costNote}`
            : `Queued for sending to ${total} recipient${total === 1 ? '' : 's'}${pairs.length > 1 ? ' across both channels' : ''}${costNote}`);
        }
        // Partial failure: the other channel may already be queued — say so explicitly
        for (const f of failures) toast.error(f, { duration: 8000 });
      } else {
        toast.success(pairs.length > 1 ? 'Drafts saved (WhatsApp + Email)' : 'Draft saved');
      }
      nav('/broadcasts');
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    setSaving(false);
  }

  async function sendTest() {
    const needsWA    = form.channel === 'whatsapp' || form.channel === 'both';
    const needsEmail = form.channel === 'email'    || form.channel === 'both';
    if (needsWA && !testPhone.trim()) return toast.error('Enter a phone number for the WhatsApp test');
    if (needsEmail && !testEmail.trim()) return toast.error('Enter an email address for the email test');
    if (needsWA && !form.template_name) return toast.error('Select a WhatsApp template first');
    if (needsWA && headerNeedsMedia && !form.wa_header_media_id)
      return toast.error(`This template needs a header ${tHeader.format.toLowerCase()} — upload it first`);
    if (needsWA && headerNeedsText && !form.wa_header_text.trim())
      return toast.error('This template needs a header text value');
    if (needsEmail && !form.subject) return toast.error('Enter an email subject first');

    setTestSending(true);
    try {
      const r = await api.post('/broadcasts/test', {
        ...form,
        template_params: form.template_params.map(s => (s || '').trim()),
        template_param_fallbacks: (form.template_param_fallbacks || []).map(s => (s || '').trim()),
        phone: testPhone,
        email: testEmail,
      });
      const { whatsapp, email } = r.data;
      if (whatsapp) whatsapp.ok ? toast.success(`WhatsApp test sent to ${testPhone}`) : toast.error(`WhatsApp: ${whatsapp.error}`, { duration: 8000 });
      if (email) email.ok ? toast.success(`Email test sent to ${testEmail}`) : toast.error(`Email: ${email.error}`, { duration: 8000 });
      if ((!whatsapp || whatsapp.ok) && (!email || email.ok)) setTestOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Test send failed');
    }
    setTestSending(false);
  }

  const selectedTemplate = templates.find(t => t.name === form.template_name);
  const tComps = selectedTemplate?.components || [];
  const tBody = tComps.find(c => c.type === 'BODY')?.text || '';
  const tHeader = tComps.find(c => c.type === 'HEADER');
  const paramCount = selectedTemplate ? detectParamCount(tBody) : 0;
  const headerNeedsMedia = !!(selectedTemplate && tHeader && tHeader.format !== 'TEXT');
  const headerNeedsText = !!(selectedTemplate && tHeader?.format === 'TEXT' && detectParamCount(tHeader.text) > 0);

  // Body text with {{n}} resolved from the selected value or fallback.
  const previewBodyValues = Array.from({ length: paramCount }, (_, i) => fillSample(
    (form.template_params[i] || '').trim() || ((form.template_param_fallbacks || [])[i] || '').trim()
  ));

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">New Broadcast</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Basics */}
          <div className="card">
            <div className="card-title">Details</div>
            <div className="form-group">
              <label className="label">Broadcast Name *</label>
              <input className="input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. June Album Drop" />
            </div>
            <div className="form-group">
              <label className="label">Channel</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { value: 'whatsapp', label: '📱 WhatsApp' },
                  { value: 'email',    label: '📧 Email' },
                  { value: 'both',     label: '📱📧 Both' },
                ].filter(option => !sharedWaba || option.value === 'whatsapp').map(({ value, label }) => (
                  <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="radio" name="channel" value={value} checked={form.channel === value} onChange={() => set('channel', value)} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {sharedWaba && <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>This shared WABA workspace sends WhatsApp broadcasts only.</p>}
              {form.channel === 'both' && (
                <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                  Creates a WhatsApp and an email broadcast as a pair — each tracked with its own stats.
                  Contacts are reached on every channel they have details for.
                  The email content is auto-filled from your WhatsApp template (you can still edit it).
                </p>
              )}
            </div>
          </div>

          {/* WhatsApp content */}
          {(form.channel === 'whatsapp' || form.channel === 'both') && (
            <div className="card">
              <div className="card-title">WhatsApp Template</div>

              {/* Error banner */}
              {templateError && (
                <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, lineHeight: 1.8 }}>
                  <div style={{ fontWeight: 600, color: 'var(--danger)', marginBottom: 6 }}>⚠️ Could not load templates</div>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 10 }}>{templateError}</div>
                  <Link to="/settings" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
                    🔍 Go to Settings → Auto-detect IDs
                  </Link>
                </div>
              )}

              {/* Loaded templates dropdown */}
              {templates.length > 0 && (
                <div className="form-group">
                  <label className="label">Select Approved Template ({templates.length} available)</label>
                  <select className="select" value={form.template_name} onChange={e => set('template_name', e.target.value)}>
                    <option value="">— choose template —</option>
                    {templates.map(t => <option key={t.name} value={t.name}>{t.name} ({t.language})</option>)}
                  </select>
                </div>
              )}

              {/* No templates but no error either (connected, just none approved yet) */}
              {!templateError && templates.length === 0 && (
                <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
                  ℹ️ No approved templates found in your WABA. Create and get them approved in{' '}
                  <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-light)' }}>Meta Business Manager</a>,
                  then enter the name manually below.
                </div>
              )}
              <div className="form-group">
                <label className="label">Template Name (exact)</label>
                <input className="input" value={form.template_name} onChange={e => set('template_name', e.target.value)} placeholder="hello_world" />
              </div>
              <div className="form-group">
                <label className="label">Language Code</label>
                <input className="input" value={form.template_language} onChange={e => set('template_language', e.target.value)} placeholder="en_US" />
              </div>
              {/* Template header — media upload or text value, when the template declares one */}
              {selectedTemplate && tHeader && tHeader.format !== 'TEXT' && (
                <div className="form-group">
                  <label className="label">Header {tHeader.format.toLowerCase()} — required by this template</label>
                  <WAHeaderUpload format={tHeader.format}
                    mediaId={form.wa_header_media_id}
                    previewUrl={form.wa_header_preview}
                    onChange={m => setForm(f => ({
                      ...f,
                      wa_header_media_id: m?.media_id || '',
                      wa_header_type: m?.type || '',
                      wa_header_preview: m?.url || '',
                    }))} />
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, marginBottom: 0 }}>
                    Stored on WhatsApp's own servers (free) — valid for 30 days, so upload again if you reuse this draft much later.
                  </p>
                </div>
              )}
              {selectedTemplate && tHeader?.format === 'TEXT' && detectParamCount(tHeader.text) > 0 && (
                <div className="form-group">
                  <label className="label">Header text value — required by this template</label>
                  <input className="input" value={form.wa_header_text}
                    onChange={e => set('wa_header_text', e.target.value)}
                    placeholder="Value for the header's {{1}}" />
                </div>
              )}

              {/* Parameters — auto-detected from the template body when one is selected */}
              {selectedTemplate && paramCount === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>✓ This template has no parameters.</p>
              ) : (
                <div className="form-group">
                  <label className="label">
                    {selectedTemplate
                      ? `Body Parameters — ${paramCount} detected`
                      : 'Body Parameters ({{1}}, {{2}}…)'}
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: '6px 8px', alignItems: 'center' }}>
                    <span />
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Value (can use {'{{name}}'}, {'{{phone}}'}, {'{{email}}'})</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Fallback if value is empty</span>
                    {(selectedTemplate ? Array.from({ length: paramCount }, (_, i) => form.template_params[i] || '') : form.template_params).map((p, i) => (
                      <React.Fragment key={i}>
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{`{{${i + 1}}}`}</span>
                        <input className="input" value={p}
                          onChange={e => setParam(i, e.target.value)} placeholder={i === 0 ? '{{name}}' : 'value'} />
                        <input className="input" value={(form.template_param_fallbacks || [])[i] || ''}
                          onChange={e => setFallback(i, e.target.value)} placeholder={i === 0 ? 'there' : 'fallback'} />
                      </React.Fragment>
                    ))}
                  </div>
                  {!selectedTemplate && (
                    <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}
                      onClick={() => {
                        set('template_params', [...form.template_params, '']);
                      }}>+ Add parameter</button>
                  )}
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, marginBottom: 0 }}>
                    Contact placeholders are filled per recipient. If a contact is missing that field, the fallback is sent instead.
                  </p>
                </div>
              )}

              {/* Live preview */}
              {selectedTemplate && (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="label">Preview</label>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0' }}>
                    <TemplatePhonePreview template={selectedTemplate} businessName={businessName}
                      bodyValues={previewBodyValues} headerValues={[fillSample(form.wa_header_text.trim())]}
                      mediaUrl={form.wa_header_preview} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email content */}
          {(form.channel === 'email' || form.channel === 'both') && (
            <div className="card">
              <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Email Content</span>
                {form.channel === 'both' && selectedTemplate && (
                  emailAuto ? (
                    <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--success)' }}>
                      ✨ Mirroring the WhatsApp template
                    </span>
                  ) : (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEmailAuto(true)}>
                      ↻ Re-sync from template
                    </button>
                  )
                )}
              </div>
              {form.channel === 'both' && selectedTemplate && emailAuto && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0 }}>
                  Subject and body are auto-filled from the WhatsApp template with your parameters applied.
                  Edit any field below to take over manually.
                </p>
              )}
              <div className="form-group">
                <label className="label">Header media (optional)</label>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
                  <MediaUpload kind="image" value={form.email_header_image} onChange={v => set('email_header_image', v)} />
                  <MediaUpload kind="video" value={form.email_header_video} onChange={v => set('email_header_video', v)} />
                </div>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, marginBottom: 0 }}>
                  The image shows at the top of the email. The video is added as a "▶ Watch the video" button —
                  email apps don't play embedded video. Max 25 MB each.
                </p>
              </div>
              <div className="form-group">
                <label className="label">Subject *</label>
                <input className="input" value={form.subject}
                  onChange={e => { setEmailAuto(false); set('subject', e.target.value); }}
                  placeholder="Your subject line" />
              </div>
              <div className="form-group">
                <label className="label">Plain Text Body</label>
                <textarea className="textarea" rows={4} value={form.text_body}
                  onChange={e => { setEmailAuto(false); set('text_body', e.target.value); }}
                  placeholder="Plain text fallback…" />
              </div>
              <div className="form-group">
                <label className="label">HTML Body</label>
                <textarea className="textarea" rows={10} value={form.html_body}
                  onChange={e => { setEmailAuto(false); set('html_body', e.target.value); }}
                  placeholder="<p>Hello {{name}},</p>" style={{ fontFamily: 'monospace', fontSize: 13 }} />
              </div>
            </div>
          )}

          {/* Link shortener */}
          <div className="card">
            <div className="card-title">🔗 Tracked Short Links</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input className="input" value={newLinkUrl} onChange={e => setNewLinkUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createShortLink(); } }}
                placeholder="https://yourlink.com" disabled={shorteningLink} />
              <button type="button" className="btn btn-primary btn-sm" onClick={createShortLink} disabled={shorteningLink}>
                {shorteningLink ? 'Shortening…' : 'Shorten'}
              </button>
            </div>
            {links.length > 0 && (
              <div style={{ fontSize: 13 }}>
                {links.slice(0, 8).map(l => (
                  <div key={l._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--primary-light)', wordBreak: 'break-all' }}>{l.short_url}</span>
                    <span style={{ whiteSpace: 'nowrap', marginLeft: 12 }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => copyShortLink(l.short_url)}>Copy</button>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{l.click_count} clicks</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar: targeting + actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-title">Audience</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
              <input type="checkbox" checked={form.all_contacts} onChange={e => set('all_contacts', e.target.checked)} />
              <span>All opted-in contacts</span>
            </label>
            {!form.all_contacts && (
              <div>
                <label className="label">Filter by Tags</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {tags.map(t => (
                    <label key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.tags.includes(t._id)}
                        onChange={e => set('tags', e.target.checked ? [...form.tags, t._id] : form.tags.filter(x => x !== t._id))} />
                      <span className="tag-chip" style={{ color: t.color, borderColor: t.color }}>{t.name}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{t.contact_count}</span>
                    </label>
                  ))}
                  {tags.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tags yet — create them in Contacts</span>}
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title">💰 Estimated Cost</div>
            {!estimate ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Calculating…</div>
            ) : (
              <div style={{ fontSize: 13 }}>
                {estimate.whatsapp && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginBottom: 6 }}>
                      <span>📱 WhatsApp · {estimate.whatsapp.recipients} recipient{estimate.whatsapp.recipients === 1 ? '' : 's'}</span>
                      <span>{fmtMoney(estimate.whatsapp.estimated_cost)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--primary-light)', fontWeight: 700, marginBottom: 6 }}>
                      <span>Platform token charge</span>
                      <span>{new Intl.NumberFormat().format(estimate.whatsapp.estimated_tokens || 0)} tokens</span>
                    </div>
                    {estimate.whatsapp.breakdown.map(row => (
                      <div key={row.prefix || row.country} style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', fontSize: 12, padding: '2px 0 2px 12px' }}>
                        <span>{row.country}{row.prefix && row.prefix !== 'OTHER' ? ` (+${row.prefix})` : ''} — {row.count} × {fmtMoney(row.rate)}</span>
                        <span>{fmtMoney(row.cost)}</span>
                      </div>
                    ))}
                  </>
                )}
                {estimate.email && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: estimate.whatsapp ? 8 : 0 }}>
                    <span>📧 Email · {estimate.email.recipients} recipient{estimate.email.recipients === 1 ? '' : 's'}</span>
                    <span style={{ color: 'var(--success)' }}>Free</span>
                  </div>
                )}
                {estimate.whatsapp && estimate.email && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
                    <span>Total</span>
                    <span>{fmtMoney(estimate.whatsapp.estimated_cost)}</span>
                  </div>
                )}
                <p style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 10, marginBottom: 0, lineHeight: 1.6 }}>
                  Based on configured {String(form.template_category || 'marketing').toLowerCase()} USD rates and markups
                  by recipient dialing prefix. Email goes through your own SMTP. Actual Meta billing may differ.
                </p>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title">Actions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button className="btn btn-ghost" disabled={saving}
                onClick={() => { if (!testEmail && user?.email) setTestEmail(user.email); setTestOpen(true); }}>
                🧪 Send Test
              </button>
              <button className="btn btn-ghost" onClick={() => handleSave('draft')} disabled={saving}>💾 Save as Draft</button>
              <button className="btn btn-success" onClick={() => handleSave('send')} disabled={saving}>▶ Save &amp; Send Now</button>
            </div>
            <div className="form-group" style={{ marginTop: 14, marginBottom: 0 }}>
              <label className="label">⏰ Or schedule for later</label>
              <input className="input" type="datetime-local" value={scheduledAt}
                onChange={e => setScheduledAt(e.target.value)} />
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                onClick={() => handleSave('schedule')} disabled={saving}>📅 Schedule</button>
            </div>
          </div>

          <div className="card" style={{ background: 'rgba(99,102,241,0.08)', borderColor: 'var(--primary)' }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
              <strong style={{ color: 'var(--primary-light)' }}>Compliance reminder</strong><br />
              Only opted-in contacts will receive broadcasts. Contacts who reply STOP are automatically opted out.
            </div>
          </div>
        </div>
      </div>

      {/* Test-send modal */}
      {testOpen && (
        <div onClick={() => !testSending && setTestOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div onClick={e => e.stopPropagation()} className="card"
            style={{ width: '100%', maxWidth: 400, boxShadow: '0 20px 50px rgba(0,0,0,0.4)' }}>
            <div className="card-title">🧪 Send a Test Message</div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
              Sends the current content immediately — nothing is saved and no broadcast is created.
              The test contact has no name, so you'll see your fallback values in action.
            </p>
            {(form.channel === 'whatsapp' || form.channel === 'both') && (
              <div className="form-group">
                <label className="label">📱 WhatsApp number (with country code)</label>
                <input className="input" value={testPhone} autoFocus
                  onChange={e => setTestPhone(e.target.value)} placeholder="+263 77 123 4567" />
              </div>
            )}
            {(form.channel === 'email' || form.channel === 'both') && (
              <div className="form-group">
                <label className="label">📧 Email address</label>
                <input className="input" type="email" value={testEmail}
                  onChange={e => setTestEmail(e.target.value)} placeholder="you@example.com" />
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <button className="btn btn-ghost" onClick={() => setTestOpen(false)} disabled={testSending}>Cancel</button>
              <button className="btn btn-success" onClick={sendTest} disabled={testSending}>
                {testSending ? 'Sending…' : '▶ Send test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
